import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import CustomSelect from '../../../components/common/CustomSelect';
import CustomInput from '../../../components/common/CustomInput';
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance';
import { useFestivalContext } from '../../../context/festival';

const validationSchema = Yup.object({
  festImages: Yup.array().min(1, 'At least one image is required').required('Fest is required'),
  festName: Yup.string().trim().required('Festival name is required'),
  dateOfPost: Yup.string().required('Date is required'), 
});

const Add = () => {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [manualFestName, setManualFestName] = useState('');
  const [isManualEntry, setIsManualEntry] = useState(false);

  const { festival } = useFestivalContext();

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitted(true);
    try {
      const formData = new FormData();
      formData.append('festName', isManualEntry ? manualFestName : values.festName);
      formData.append('dateOfPost', values.dateOfPost);
      values.festImages.forEach((file) => {
        formData.append('festImages', file);
      });

      const response = await axiosAuthInstance.post('festival/add', formData);

      if (response.status === 200) {
        resetForm();
        navigate('/festival');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitted(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      festImages: [],
      festName: '',
      dateOfPost: '',
    },
    validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm();
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers);
      } else {
        formikHelpers.setTouched(error);
      }
    },
  });

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files.length === 0) return;

    const fileArray = Array.from(files);
    formik.setFieldValue('festImages', fileArray);

    const previews = fileArray.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const festivalOptions = festival.map((festItem) => ({
    value: festItem.summary,
    label: festItem.summary,
  }));

  useEffect(() => {
    if (!isManualEntry && formik.values.festName && !formik.values.dateOfPost) {
      const selectedFestival = festival.find((fest) => fest.summary === formik.values.festName);
      if (selectedFestival) {
        const formattedDate = selectedFestival.start.date.split('T')[0];
        formik.setFieldValue('dateOfPost', formattedDate);
      }
    }
  }, [formik.values.festName, isManualEntry, festival]);

  return (
    <FormikProvider value={formik}>
      <form className='flex flex-col p-4 gap-4' onSubmit={formik.handleSubmit}>
        <div className='w-full'>
          <h5 className='text-sm sm:text-base font-normal'>
            Festival or Event Images<span className='text-[#D34053]'>*</span>
          </h5>
          <div className='w-full border rounded-lg p-[9px]'>
            <input type='file' name='festImages' accept='image/*' multiple onChange={handleFileChange} />
            {formik.errors.festImages && <div className='text-red-500'>{formik.errors.festImages}</div>}
          </div>
          <div className='flex gap-2 flex-wrap mt-2'>
            {imagePreviews.map((src, index) => (
              <img key={index} src={src} alt='preview' className='w-32 h-20 object-cover rounded' />
            ))}
          </div>
        </div>

        <div className='w-full'>
          {isManualEntry && (
          <h5 className='text-sm sm:text-base font-normal'>
            Festival or Event Name<span className='text-[#D34053]'>*</span>
          </h5>
          )
            }
          {!isManualEntry ? (
            <Field
              name='festName'
              required
              label='Festival or Event Name'
              component={CustomSelect}
              options={festivalOptions}
              onChange={(option) => formik.setFieldValue('festName', option.value)}
            />
          ) : (
            <input
              type='text'
              name='festName'
              placeholder='Enter Festival or Event Name'
              value={manualFestName}
              onChange={(e) => {
                setManualFestName(e.target.value);
                formik.setFieldValue('festName', e.target.value);
              }}
              className='border rounded-lg p-2 w-full'
            />
          )}
          <button
            type='button'
            className='text-blue-500 mt-2 underline'
            onClick={() => {
              setIsManualEntry(!isManualEntry);
              setManualFestName('');
              formik.setFieldValue('festName', '');
            }}
          >
            {isManualEntry ? 'Select from List' : 'Add Manually'}
          </button>
          {formik.errors.festName && <div className='text-red-500'>{formik.errors.festName}</div>}
        </div>
        
        <Field
          name='dateOfPost'
          label='Post Date'
          placeholder='Enter Post Date'
          type='date'
          component={CustomInput}
          onChange={(e) => formik.setFieldValue('dateOfPost', e.target.value)}
        />

        <button
          type='submit'
          disabled={isSubmitted}
          className='bg-primary text-xl font-medium text-white rounded-lg px-6 py-2'
        >
          {isSubmitted ? (
            <div className='w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent'></div>
          ) : (
            'Add'
          )}
        </button>
      </form>
    </FormikProvider>
  );
};

export default Add;
